const ACTIVATION_PEPPER = 'SmartAccount.OfflineActivation.v1.2026'
const VERSION_PREFIX = 'SA1'

const SHA256_K = [
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1, 0x923f82a4, 0xab1c5ed5,
  0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3, 0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174,
  0xe49b69c1, 0xefbe4786, 0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147, 0x06ca6351, 0x14292967,
  0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13, 0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85,
  0xa2bfe8a1, 0xa81a664b, 0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a, 0x5b9cca4f, 0x682e6ff3,
  0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208, 0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]

function rotr(n: number, x: number): number {
  return (x >>> n) | (x << (32 - n))
}

function sha256Bytes(message: Uint8Array): Uint8Array {
  const bitLen = message.length * 8
  const withOne = message.length + 1
  const paddedLen = (withOne + 8 + 63) & ~63
  const padded = new Uint8Array(paddedLen)
  padded.set(message)
  padded[message.length] = 0x80
  const view = new DataView(padded.buffer)
  view.setUint32(paddedLen - 4, bitLen, false)

  let h0 = 0x6a09e667
  let h1 = 0xbb67ae85
  let h2 = 0x3c6ef372
  let h3 = 0xa54ff53a
  let h4 = 0x510e527f
  let h5 = 0x9b05688c
  let h6 = 0x1f83d9ab
  let h7 = 0x5be0cd19
  const w = new Uint32Array(64)

  for (let offset = 0; offset < paddedLen; offset += 64) {
    for (let i = 0; i < 16; i++) w[i] = view.getUint32(offset + i * 4, false)
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(7, w[i - 15]) ^ rotr(18, w[i - 15]) ^ (w[i - 15] >>> 3)
      const s1 = rotr(17, w[i - 2]) ^ rotr(19, w[i - 2]) ^ (w[i - 2] >>> 10)
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0
    }

    let a = h0
    let b = h1
    let c = h2
    let d = h3
    let e = h4
    let f = h5
    let g = h6
    let h = h7

    for (let i = 0; i < 64; i++) {
      const S1 = rotr(6, e) ^ rotr(11, e) ^ rotr(25, e)
      const ch = (e & f) ^ (~e & g)
      const temp1 = (h + S1 + ch + SHA256_K[i] + w[i]) >>> 0
      const S0 = rotr(2, a) ^ rotr(13, a) ^ rotr(22, a)
      const maj = (a & b) ^ (a & c) ^ (b & c)
      const temp2 = (S0 + maj) >>> 0
      h = g
      g = f
      f = e
      e = (d + temp1) >>> 0
      d = c
      c = b
      b = a
      a = (temp1 + temp2) >>> 0
    }

    h0 = (h0 + a) >>> 0
    h1 = (h1 + b) >>> 0
    h2 = (h2 + c) >>> 0
    h3 = (h3 + d) >>> 0
    h4 = (h4 + e) >>> 0
    h5 = (h5 + f) >>> 0
    h6 = (h6 + g) >>> 0
    h7 = (h7 + h) >>> 0
  }

  const out = new Uint8Array(32)
  const outView = new DataView(out.buffer)
  outView.setUint32(0, h0, false)
  outView.setUint32(4, h1, false)
  outView.setUint32(8, h2, false)
  outView.setUint32(12, h3, false)
  outView.setUint32(16, h4, false)
  outView.setUint32(20, h5, false)
  outView.setUint32(24, h6, false)
  outView.setUint32(28, h7, false)
  return out
}

function hmacSha256Bytes(key: Uint8Array, message: Uint8Array): Uint8Array {
  const block = 64
  let k = key.length > block ? sha256Bytes(key) : key
  const keyPad = new Uint8Array(block)
  keyPad.set(k)
  const ipad = new Uint8Array(block + message.length)
  const opad = new Uint8Array(block + 32)
  for (let i = 0; i < block; i++) {
    ipad[i] = keyPad[i] ^ 0x36
    opad[i] = keyPad[i] ^ 0x5c
  }
  ipad.set(message, block)
  opad.set(sha256Bytes(ipad), block)
  return sha256Bytes(opad)
}

function toHexUpper(bytes: Uint8Array): string {
  return [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('').toUpperCase()
}

function hmacSha256Hex(message: string): string {
  const enc = new TextEncoder()
  return toHexUpper(hmacSha256Bytes(enc.encode(ACTIVATION_PEPPER), enc.encode(message)))
}

export function normalizeDeviceCode(input?: string | null): string | null {
  if (!input) return null
  const digits = input.replace(/\D/g, '')
  return digits.length === 12 ? digits : null
}

export function formatDeviceCode(digits: string): string {
  const normalized = normalizeDeviceCode(digits)
  if (!normalized) return digits.trim()
  return `${normalized.slice(0, 4)}-${normalized.slice(4, 8)}-${normalized.slice(8, 12)}`
}

export function deviceCodeFromHardDiskId(hardDiskId: string): string {
  const hash = sha256Bytes(new TextEncoder().encode(hardDiskId.trim()))
  let n = 0n
  for (let i = 0; i < 8; i++) n = (n << 8n) + BigInt(hash[i])
  return (n % 1_000_000_000_000n).toString().padStart(12, '0')
}

export function uniqueIdForDevice(deviceCode: string): string {
  const digits = normalizeDeviceCode(deviceCode)
  if (!digits) throw new Error('رقم الجهاز يجب أن يكون 12 رقماً')
  return `DEV-${digits}`
}

export function extractDeviceCode(input?: string | null): string | null {
  if (!input) return null
  const trimmed = input.trim()
  if (trimmed.toUpperCase().startsWith('DEV-')) {
    return normalizeDeviceCode(trimmed.slice(4))
  }
  return normalizeDeviceCode(trimmed)
}

export function generateActivationCode(
  deviceCode: string,
  days: number,
  issuedUtc: Date = new Date(),
): string {
  const digits = normalizeDeviceCode(deviceCode)
  if (!digits) throw new Error('رقم الجهاز يجب أن يكون 12 رقماً')
  if (!Number.isFinite(days) || days < 1 || days > 36500) {
    throw new Error('عدد الأيام غير صالح')
  }

  const y = issuedUtc.getUTCFullYear()
  const m = String(issuedUtc.getUTCMonth() + 1).padStart(2, '0')
  const d = String(issuedUtc.getUTCDate()).padStart(2, '0')
  const issued = `${y}${m}${d}`
  const hmac = hmacSha256Hex(`${digits}|${days}|${issued}`).slice(0, 10)
  return `${VERSION_PREFIX}-${digits}-${String(days).padStart(4, '0')}-${issued}-${hmac}`
}
